import { useState, useEffect } from 'react';
import { Card, LoadingSpinner } from '../Layout';
import { api } from '../../services/api';

interface LaborWorkstation {
  workstationCode: string;
  workstationName: string;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  startedByName: string | null;
  completedByName: string | null;
  durationMinutes: number | null;
  /** True when duration was estimated from prev-station completion timestamp
   *  (worker didn't press Start before completing the card). */
  durationEstimated?: boolean;
  /** Where the duration came from:
   *   - measured: completed_at - started_at
   *   - estimated_from_prev: completed_at - prev station's completed_at (capped 60 min)
   *   - expected: ws.expected_minutes_per_unit (admin-configured budget)
   *   - in_progress: NOW() - started_at while station is running
   *   - unknown: no signal available
   */
  durationSource?: 'measured' | 'estimated_from_prev' | 'expected' | 'in_progress' | 'unknown';
  expectedMinutesPerUnit?: number | null;
  hourlyCost: number;
  laborCost: number | null;
}

interface LaborItem {
  itemNumber: number;
  orderItemId: string;
  totalMinutes: number;
  totalCost: number;
  workstations: LaborWorkstation[];
}

interface LaborBomData {
  items: LaborItem[];
  summary: {
    totalMinutes: number;
    totalCost: number;
    totalWorkOrders: number;
    completedWorkOrders: number;
  };
}

function formatDuration(minutes: number | null): string {
  if (minutes == null || minutes <= 0) return '\u2014';
  const hrs = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  const secs = Math.round((minutes % 1) * 60);
  if (hrs > 0) return `${hrs}h ${mins}m`;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

function formatCost(cost: number | null): string {
  if (cost == null) return '\u2014';
  return `$${cost.toFixed(2)}`;
}

function getElapsedMinutes(startedAt: string): number {
  const start = new Date(startedAt).getTime();
  const now = Date.now();
  return (now - start) / 60000;
}

function statusIcon(status: string): string {
  switch (status) {
    case 'completed': return '\u2713';
    case 'in_progress': return '\u23f3';
    case 'skipped': return '\u2298';
    default: return '\u25cb';
  }
}

function statusColor(status: string): string {
  switch (status) {
    case 'completed':
      return 'bg-green-50 text-green-800';
    case 'in_progress':
      return 'bg-yellow-50 text-yellow-800';
    case 'skipped':
      return 'bg-gray-100 text-gray-400 opacity-60';
    default:
      return 'bg-gray-50 text-gray-500';
  }
}

export function LaborBomTab({ orderId }: { orderId: string }) {
  const [data, setData] = useState<LaborBomData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [, setTick] = useState(0);

  useEffect(() => {
    loadData();
  }, [orderId]);

  // Tick every 30s to update in-progress timers
  useEffect(() => {
    const hasInProgress = data?.items.some(item =>
      item.workstations.some(ws => ws.status === 'in_progress')
    );
    if (!hasInProgress) return;
    const interval = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(interval);
  }, [data]);

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await api.getOrderLaborBom(orderId);
      setData(result);
      // Expand all items by default
      const allItems = new Set<number>(result.items.map((i: LaborItem) => i.itemNumber));
      setExpandedItems(allItems);
    } catch (err) {
      console.error('Failed to load labor BOM:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (itemNumber: number) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemNumber)) next.delete(itemNumber);
      else next.add(itemNumber);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <Card>
        <div className="text-center py-12">
          <svg className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-500 dark:text-gray-400 text-lg">No labor data yet</p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Labor tracking begins when the order enters production</p>
        </div>
      </Card>
    );
  }

  const { summary } = data;
  const progressPct = summary.totalWorkOrders > 0
    ? Math.round((summary.completedWorkOrders / summary.totalWorkOrders) * 100)
    : 0;

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Labor Summary</h3>
          <button
            onClick={loadData}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            title="Refresh"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{formatDuration(summary.totalMinutes)}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Total Time</div>
          </div>
          <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{formatCost(summary.totalCost)}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Total Labor Cost</div>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{summary.completedWorkOrders}/{summary.totalWorkOrders}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Stations Complete</div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">{progressPct}%</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Progress</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div
            className="bg-blue-500 h-2 rounded-full transition-all"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </Card>

      {/* Per-Unit Sections */}
      {data.items.map(item => {
        const expanded = expandedItems.has(item.itemNumber);
        return (
          <Card key={item.itemNumber}>
            {/* Unit Header */}
            <button
              onClick={() => toggleItem(item.itemNumber)}
              className="w-full flex items-center justify-between py-1"
            >
              <div className="flex items-center gap-3">
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className="text-base font-semibold text-gray-900 dark:text-white">
                  Unit {item.itemNumber}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {item.workstations.filter(ws => ws.status === 'completed').length}/{item.workstations.length} stations
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-blue-600 dark:text-blue-400 font-medium">{formatDuration(item.totalMinutes)}</span>
                <span className="text-green-600 dark:text-green-400 font-medium">{formatCost(item.totalCost)}</span>
              </div>
            </button>

            {/* Workstation Rows */}
            {expanded && (
              <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                {/* Header Row */}
                <div className="grid grid-cols-12 gap-2 px-2 pb-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="col-span-3">Station</div>
                  <div className="col-span-3">Worker</div>
                  <div className="col-span-2 text-right">Time</div>
                  <div className="col-span-2 text-right">Rate</div>
                  <div className="col-span-2 text-right">Cost</div>
                </div>

                {/* Data Rows */}
                {item.workstations.map((ws, idx) => {
                  const isInProgress = ws.status === 'in_progress';
                  const elapsed = isInProgress && ws.startedAt ? getElapsedMinutes(ws.startedAt) : null;
                  const workerName = ws.completedByName || ws.startedByName;

                  return (
                    <div
                      key={idx}
                      className={`grid grid-cols-12 gap-2 px-2 py-2 rounded-lg mb-1 text-sm ${statusColor(ws.status)}`}
                    >
                      <div className="col-span-3 flex items-center gap-1.5 font-medium">
                        <span className="text-xs">{statusIcon(ws.status)}</span>
                        <span className="truncate" title={ws.workstationName}>{ws.workstationCode}</span>
                      </div>
                      <div className="col-span-3 truncate">
                        {workerName || '\u2014'}
                      </div>
                      <div className="col-span-2 text-right font-mono">
                        {ws.status === 'skipped'
                          ? <span className="text-xs italic">Skipped</span>
                          : isInProgress
                            ? <span className="text-yellow-400">{formatDuration(elapsed)}&hellip;</span>
                            : (() => {
                              const src = ws.durationSource;
                              const tag = src === 'estimated_from_prev'
                                ? { label: 'est', color: 'text-amber-500', tip: 'Estimated from previous station — worker did not press Start before completing' }
                                : src === 'expected'
                                  ? { label: 'exp', color: 'text-blue-500', tip: `Used station's configured expected time (${ws.expectedMinutesPerUnit} min/unit) — no measured or prev-station data` }
                                  : null;
                              return (
                                <span title={tag?.tip}>
                                  {formatDuration(ws.durationMinutes)}
                                  {tag && <span className={`text-[9px] ${tag.color} ml-1`}>{tag.label}</span>}
                                </span>
                              );
                            })()}
                      </div>
                      <div className="col-span-2 text-right font-mono text-xs">
                        {ws.status === 'completed' || isInProgress
                          ? `$${ws.hourlyCost.toFixed(0)}/h`
                          : '\u2014'}
                      </div>
                      <div className="col-span-2 text-right font-mono font-medium">
                        {ws.status === 'completed' ? formatCost(ws.laborCost) : '\u2014'}
                      </div>
                    </div>
                  );
                })}

                {/* Unit Total Row */}
                <div className="grid grid-cols-12 gap-2 px-2 py-2 mt-1 border-t border-gray-200 dark:border-gray-700 text-sm font-semibold">
                  <div className="col-span-3 text-gray-700 dark:text-gray-300">Total</div>
                  <div className="col-span-3"></div>
                  <div className="col-span-2 text-right text-blue-600 dark:text-blue-400 font-mono">
                    {formatDuration(item.totalMinutes)}
                  </div>
                  <div className="col-span-2"></div>
                  <div className="col-span-2 text-right text-green-600 dark:text-green-400 font-mono">
                    {formatCost(item.totalCost)}
                  </div>
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
